import {
	Asset,
	Journey,
	Location,
	Organisation,
	Person
} from './'

const uuidrx = /^[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}$/;

export class Entity {
	constructor (payload, api) {
		Object.defineProperty(this, 'api', {value: api}); // Make api non-enumerable
		this.id = payload.id;
		this.type = payload.type;
		this.data = payload.data || {};
		this.private = payload.private || {};
		this.relations = undefined;
		this.memberOf = payload.memberOf || [];
		this.updatedBy = payload.updatedBy;
		this.updatedOn = payload.updatedOn;
		this.createdOn = payload.createdOn;
		this.computed = {};
	}

	/**
	 * Expands entity values
	 *
	 * By default it also expands relations unless { relations: false }
	 * is given.
	 *
	 * Modifies the object and returns it asynchronously.
	 */
	async expand (opts) {
		if (!opts) opts = {};

		if (!this.relations && opts.relations) {
			let relations = [];
			let triples = [ ...await this.api.triple(this.id), ...await this.api.triple(null, this.id) ];
			for (let triple of triples) {
				let expandedTriple = {};
				if (triple.subject !== this.id) {
					expandedTriple.subject = await this.api.entityByUuid(triple.subject);
					await expandedTriple.subject.expand({relations: false});
				}
				if (triple.object !== this.id) {
					expandedTriple.object = await this.api.entityByUuid(triple.object);
					await expandedTriple.object.expand({relations: false});
				}
				expandedTriple.predicate = predicate;
				relations.push(expandedTriple);
			}
			this.relations = relations;
		}

		if (this.memberOf) {
			for (let index in this.memberOf) {
				let element = this.memberOf[index];
				if (typeof element == 'string') {
					this.memberOf[index] = this.api ?
						await this.api.entityByUuid(element) :
						new Entity({id: element});
				}
			}
		}

		return this;
	}

	displayName () {
		if (this.data.name) {
			if (typeof this.data.name === 'string') {
				return this.data.name;
			} else if (Array.isArray(this.data.name)) {
				return this.data.name[0];
			}
		} else {
			return this.id;
		}
	}

	async expandUUID (obj = this) {
		for (const key in obj) {
			if (key == "id") {
				continue;
			}
			const val = obj[key];
			if (typeof val === 'object') {
				await this.expandUUID(val);
			} else if (typeof val === 'string' && uuidrx.test(val)) {
// 				console.log("→ UUID", val);
				const cached = await this.api.cache.get(val);
				if (cached) {
// 					console.log("ALREADY SEEN");
					obj[key] = cached;
				} else {
// 					console.log("FETCHING", val);
					const entity = await this.api.entityByUuid(val);
					this.api.cache.put(entity);
// 					console.log("FETCHED");
					obj[key] = entity;
				}
			}
		}
		return obj;
	}

	/**
	 * Determine entity visibility
	 *
	 * @arg other Another entity
	 *
	 * As a general rule, entities are visible to one another
	 * only if they share at least one common membership.
	 *
	 * Entities with permission "master" however can see any
	 * other entity.
	 */
	isVisibleBy (other) {
		if (this.memberOf) {
			if (other.memberOf) {
				if (this.memberOf.filter( e => other.memberOf.includes(e) ).length) {
					return true;
				}
			}
		}
		if (other.permissions) {
			if (other.permissions.includes("master")) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Determine if an entity is editable by another
	 *
	 * @arg other Another entity
	 *
	 * As a general rule, entities are only editable by
	 * another entity only if they are visible to that
	 * entity and that entity has the "master" role.
	 *
	 */
	isEditableBy (other) {
		if (this.isVisibleBy(other)) {
			if (other.permissions) {
				if (other.permissions.includes("master")) {
					return true;
				}
			}
		}
		return false;
	}

	deepProp (prop, tagFn) {
		let matches = [];
		const _tagFn = tagFn ? tagFn : (obj, prop, matches) => {
			if (typeof obj.displayName == 'function') {
				return obj.displayName();
			} else {
				return prop;
			}
		};

		function _deepProp (object, name) {

			const label = name || prop;

			for (let key in object) {
				const attr = object[key];
				if (key == prop) {
					if (Array.isArray(attr)) {
						for (let item of attr) {
							matches.push({
								label,
								value: item
							});
						}
					} else {
						matches.push({
							label,
							value: attr
						});
					}
				} else if (attr instanceof Entity) {
					_deepProp(attr.data, attr.displayName());
				} else if (attr instanceof Object) {
					_deepProp(attr);
				}
			}

		}

		_deepProp(this);
		return matches;
	}

	match (regexp) {
		return Entity.match(this.data, regexp);
	}

	static match (obj, regexp) {
// 		console.log("MATCH", obj, regexp);
		if (obj === undefined || obj === null) {
			return false;
		} else if (obj.match instanceof Function && ! (obj instanceof Entity)) {
			return obj.match(regexp);
		} else {
			for (let key in obj) {
				const value = obj[key];
// 				console.log("MATCH VALUE", key, value);

				if (value === undefined || value === null) {
					continue;
				}

				if (value.match instanceof Function) {
// 					console.log("VALUE MATCH INSTANCEOF FUNCTION");
					if (value.match(regexp)) {
						return true;
					}
				} else if (value[Symbol.iterator] instanceof Function) {
// 					console.log("VALUE IS ITERABLE");
					for (let subvalue of value) {
// 						console.log("MATCH SUBVALUE", subvalue);
						if (Entity.match(subvalue, regexp)) {
							return true;
						}
					}
				} else if (value instanceof Object) {
// 					console.log("VALUE INSTANCEOF OBJECT");
					if (Entity.match(value, regexp)) {
						return true;
					}
				} else if (String(value).match(regexp)) {
// 					console.log("VALUE MATCH BY STRING");
					return true;
				}
			}
		}

		return false;
	}

	static propName (entity, prop) {
		if (entity.data[prop]) {
			const attr = entity.data[prop];
			if (typeof attr.displayName == 'function') {
				return attr.displayName();
			} else if (attr instanceof Object) {
				const jmEnt = Entity.make(attr, entity.api);
				return jmEnt.displayName();
			} else {
				return attr;
			}
		}
		return "";
	}

	static make (payload, api) {
		if (payload) {
			switch (payload.type) {
				case "organisation":
					return new Organisation(payload, api);
				case "person":
					return new Person(payload, api);
				case "asset":
					return new Asset(payload, api);
				case "location":
					return new Location(payload, api);
				case "journey":
					let j = new Journey(payload, api);
					return new Journey(payload, api);
				default:
					return new Entity(payload, api);
			}
		}
	}
}



// WEBPACK FOOTER //
// ./src/assets/js/JMan/Entity/Entity.js